// Three-mode market picker used by the Client Workspace's New Client dialog.
//
// A Client may be scoped to:
//   - Global       (no specific market)
//   - Region       (a model-defined geo block such as "North America")
//   - Country      (a specific ISO country)
//
// The component is purely controlled — it reports the chosen `ClientMarket`
// (with kind, code, and a cached friendly label) up to the parent.

import { Globe2, Map as MapIcon, MapPin } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import clsx from 'clsx';
import type { ClientMarket } from '@rc/engine/types';
import { GLOBAL_MARKET_CODE } from '@rc/engine';
import { COUNTRIES } from './CountryPicker';

interface RegionOption {
  value: string;
  label: string;
}

interface Props {
  value: ClientMarket | undefined;
  onChange: (next: ClientMarket | undefined) => void;
  /** Region options sourced from the model's `geo` dimension. Standard
   *  worldwide regions (NAMER / LATAM / EMEA / APAC) are always merged in
   *  on top of these, so the picker offers a consistent baseline regardless
   *  of how the model defines its `geo` dimension. */
  regions?: RegionOption[];
  /** Limit which market modes are shown (default: all three). */
  allowedModes?: Mode[];
}

type Mode = 'global' | 'region' | 'country';

const MODES: { id: Mode; label: string; icon: LucideIcon }[] = [
  { id: 'global', label: 'Global', icon: Globe2 },
  { id: 'region', label: 'Region', icon: MapIcon },
  { id: 'country', label: 'Country', icon: MapPin },
];

/** Canonical worldwide regions. Always available in the region select so the
 *  Client Workspace can be scoped to a coverage area without depending on
 *  the active model's `geo` dimension. Any model-supplied regions are merged
 *  in beneath these (deduped by `value`). */
export const STANDARD_REGIONS: RegionOption[] = [
  { value: 'namer', label: 'NAMER — North America' },
  { value: 'latam', label: 'LATAM — Latin America' },
  { value: 'emea', label: 'EMEA — Europe, Middle East & Africa' },
  { value: 'apac', label: 'APAC — Asia Pacific' },
];

function mergeRegions(modelRegions: RegionOption[]): RegionOption[] {
  const seen = new Set<string>();
  const out: RegionOption[] = [];
  for (const r of STANDARD_REGIONS) {
    if (seen.has(r.value)) continue;
    seen.add(r.value);
    out.push(r);
  }
  for (const r of modelRegions) {
    if (!r.value || seen.has(r.value)) continue;
    seen.add(r.value);
    out.push(r);
  }
  return out;
}

export default function MarketPicker({
  value,
  onChange,
  regions = [],
  allowedModes = ['global', 'region', 'country'],
}: Props) {
  const modes = MODES.filter((m) => allowedModes.includes(m.id));
  const mode: Mode = value?.kind || (value?.code === GLOBAL_MARKET_CODE ? 'global' : 'country');
  const merged = mergeRegions(regions);

  const setMode = (next: Mode) => {
    if (next === 'global') {
      onChange({ kind: 'global', code: GLOBAL_MARKET_CODE, label: 'Global' });
      return;
    }
    if (next === 'region') {
      const first = merged[0];
      onChange({
        kind: 'region',
        code: first?.value,
        label: first?.label || first?.value,
      });
      return;
    }
    // country
    const first = COUNTRIES[0]!;
    onChange({ kind: 'country', code: first.code, label: first.name });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-0.5">
        {modes.map((m) => {
          const Icon = m.icon;
          const active = mode === m.id;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setMode(m.id)}
              className={clsx(
                'flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                active
                  ? 'bg-white text-brand-purple-700 ring-1 ring-brand-purple-200 shadow-sm'
                  : 'text-slate-500 hover:text-slate-800',
              )}
            >
              <Icon size={13} />
              {m.label}
            </button>
          );
        })}
      </div>

      {mode === 'global' && (
        <p className="text-[11px] text-slate-500 px-1">
          Workspace is not tied to a single country or region.
        </p>
      )}

      {mode === 'region' && (
        <select
          className="input"
          value={value?.code || ''}
          onChange={(e) => {
            const code = e.target.value;
            const opt = merged.find((r) => r.value === code);
            onChange({ kind: 'region', code, label: opt?.label || code });
          }}
        >
          {merged.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      )}

      {mode === 'country' && (
        <select
          className="input"
          value={value?.code || ''}
          onChange={(e) => {
            const code = e.target.value;
            const opt = COUNTRIES.find((c) => c.code === code);
            onChange({ kind: 'country', code, label: opt?.name || code });
          }}
        >
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.code} · {c.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/** Compact label used in tables / pills. Returns "Global", "EMEA", "US", etc. */
export function formatMarketShort(m: ClientMarket | undefined): string {
  if (!m) return 'Global';
  if (m.code === GLOBAL_MARKET_CODE || m.kind === 'global') return 'Global';
  if (m.kind === 'country' || (m.code && m.code.length === 2)) return m.code || m.label || '—';
  return m.label || m.code || '—';
}

/** Verbose label used in tooltips and detail views. */
export function formatMarketFull(m: ClientMarket | undefined): string {
  if (!m) return 'Global';
  if (m.code === GLOBAL_MARKET_CODE || m.kind === 'global') return 'Global';
  if (m.kind === 'country' || (m.code && m.code.length === 2)) {
    const country = COUNTRIES.find((c) => c.code === m.code);
    return country ? `${country.name} (${country.code})` : m.label || m.code || '—';
  }
  return m.label || m.code || '—';
}
